<template>
    <div class="standings">
        <StaticSpinner v-if="!loaded" />
        <template v-else>
            <Group
                v-for="group in displayGroups"
                v-bind:key="group.groupName"
                v-bind:group="group"
                v-bind:columns="columns"
                v-bind:outrightLeague="outrightLeagueByGroup[group.rawGroupName] ?? null"
                v-bind:outrightLeagueLoaded="outrightLeaguesLoaded"
            />
        </template>
    </div>
</template>

<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { computed } from 'vue';
    import type { PropType } from 'vue';
    import StaticSpinner from '@/components/common/StaticSpinner.vue';
    import Group from '@/components/tournamentCenter/Group.vue';
    import { getTournamentCenterTeamImage } from '@/components/tournamentCenter/tournamentCenterHelper';
    import { i18n } from '@/core/lib/i18n';
    import { getTranslated } from '@/core/lib/translationHelper';
    import { useOutrightLeagues } from '@/core/oddsApi/composable/useOutrightLeagues';
    import { EnumLanguage, GroupTableColumn, SiteStyle, SportType } from '@/interface/enum';
    import type { IGroup, IGroupColumn, IGroupDisplay, IGroupTeamDisplay } from '@/interface/tournamentCenter';
    import { useToggleStore } from '@/store/toggleStore';

    const props = defineProps({
        groups: {
            type: Array as PropType<IGroup[]>,
            required: true,
        },
        loaded: {
            type: Boolean,
            required: true,
        },
    });

    const { tournamentCenterGroupWinnerLeagueName } = storeToRefs(useToggleStore(SiteStyle.Asia));

    const winnerOutrightLeagueNames = computed(() => {
        const pattern = tournamentCenterGroupWinnerLeagueName.value;
        if (!pattern) return null;
        return props.groups.map(g => pattern.replace('{group}', g.groupName));
    });

    const { outrightLeagues, loaded: outrightLeaguesLoaded } = useOutrightLeagues({
        sportType: SportType.Soccer,
        isLive: false,
        leagueNames: winnerOutrightLeagueNames,
    });

    const outrightLeagueByGroup = computed(() => {
        if (!tournamentCenterGroupWinnerLeagueName.value) {
            return {};
        }

        return props.groups.reduce<Record<string, typeof outrightLeagues.value[number]>>((map, group) => {
            const expectedName = tournamentCenterGroupWinnerLeagueName.value.replace('{group}', group.groupName);
            const league = outrightLeagues.value.find(l => getTranslated(l.name, EnumLanguage.EN).toLowerCase() === expectedName.toLowerCase());
            if (league) {
                map[group.groupName] = league;
            }
            return map;
        }, {});
    });

    const isShowWinnerColumn = computed(() => !outrightLeaguesLoaded.value || (outrightLeaguesLoaded.value && outrightLeagues.value.length > 0));

    const columnConfigs = computed<IGroupColumn[]>(() => [
        {
            key: GroupTableColumn.Played,
            title: i18n.t('tournament_center_played_short'),
            isShow: false,
        },
        {
            key: GroupTableColumn.Won,
            title: i18n.t('tournament_center_win_short'),
            isShow: true,
        },
        {
            key: GroupTableColumn.Drawn,
            title: i18n.t('tournament_center_draw_short'),
            isShow: true,
        },
        {
            key: GroupTableColumn.Lost,
            title: i18n.t('tournament_center_lose_short'),
            isShow: true,
        },
        {
            key: GroupTableColumn.GoalDifference,
            title: i18n.t('tournament_center_goal_difference_short'),
            isShow: true,
        },
        {
            key: GroupTableColumn.Points,
            title: i18n.t('tournament_center_points_short'),
            isShow: true,
        },
        {
            key: GroupTableColumn.Winner,
            title: i18n.t('tournament_center_market_type_group_winner'),
            isShow: isShowWinnerColumn.value,
        },
    ]);

    const columns = computed(() => columnConfigs.value.filter(x => x.isShow));

    const displayGroups = computed<(IGroupDisplay & { rawGroupName: string })[]>(() => props.groups.map(group => ({
        rawGroupName: group.groupName,
        groupName: i18n.t('tournament_center_group', { alphabet: group.groupName }),
        teamList: group.teamList.map<IGroupTeamDisplay>((team) => {
            const valueMapping = columns.value.reduce<Partial<Record<GroupTableColumn, number>>>((acc, { key }) => {
                if (key === GroupTableColumn.Winner) {
                    return acc;
                }
                acc[key] = team[key] ?? 0;
                return acc;
            }, {});

            return ({
                id: team.id,
                ranking: team.ranking ?? 0,
                name: getTranslated(team.name) || 'TBD',
                logo: getTournamentCenterTeamImage(team.countryName),
                valueMapping,
                countryName: team.countryName,
            });
        }),
    })));
</script>

<style lang="scss" scoped>
    @import "@/components/tournamentCenter/Group";
</style>
